// Form-shape types for filter / suppression rule sets. The mapper in
// `QuerySettingsPanel/settingsMapper.ts` is the single converter to/from the
// backend `RuleSetType` / `RuleSetInput` shapes — keep this file and its
// tables in sync.

import type { RuleValueKind } from "~/graphql/supergraphTypes";

/** Lowercase mirror of backend `RuleOperator`. */
export type FilterOperator =
  "eq" | "neq" | "gt" | "gte" | "lt" | "lte" | "in" | "not_in" | "contains";

/** Shape of `RuleClause.value` is operator-dependent:
 *  - scalar operators (`eq`/`neq`/`gt`/`gte`/`lt`/`lte`/`contains`) → string
 *  - set operators (`in`/`not_in`) → string[]
 *  - either, when nothing is selected yet → null
 *
 * `mapper.normalizeClauseValue` enforces the union on read; the value
 * editor resets to the right empty shape on operator changes that cross
 * the scalar/array boundary. */
export type RuleClauseValue = string | string[] | null;

/** Used verbatim from the schema: unlike `FilterOperator` the form has no
 * lowercase mirror for it, so a clause carries the backend enum as-is. */
export type { RuleValueKind };

export type RuleClause = {
  /** Local-only id used for keying / removal — not persisted. */
  id: string;
  /** Canonical `<table>.<column>` id (e.g. `gl_account.is_active`); the pill
   * label is derived via `findAttributeByPath`. `null` while configuring. */
  attribute: string | null;
  operator: FilterOperator;
  value: RuleClauseValue;
  /** Absent means `LITERAL` — a clause the author typed a value into. When
   * `RUNTIME_INPUT`, `value` holds a token name bound at execution. */
  valueKind?: RuleValueKind;
};

/** Set-valued operators take an array on `RuleClause.value`; everything else
 * takes a scalar. Single source of truth — read by both the mapper and the
 * value-editor branching logic. */
export const SET_OPERATORS = new Set<FilterOperator>(["in", "not_in"]);

export const isSetOperator = (op: FilterOperator): boolean => SET_OPERATORS.has(op);

/** Lowercase mirror of backend `LogicalOperator`. The UI hardcodes the
 * visible "and"/"or" labels, but we round-trip server values verbatim so
 * queries authored elsewhere aren't silently rewritten. */
export type ConjunctionOperator = "and" | "or";

export type RuleGroup = {
  id: string;
  conditionOperator: ConjunctionOperator;
  clauses: RuleClause[];
};

export type Rules = {
  groupsOperator: ConjunctionOperator;
  groups: RuleGroup[];
};
